import { ProduktQuantityButton } from '@shared/models/product-quantity-button';
import { TolgeeService } from '@core/translation/tolgee.service';
import { BoatAreaSource, resolveBoatArea } from '@shared/functions/resolve-boat-area';
import { roundNumber, RoundNumberFactor } from '@modules/bcm/@shared/pipes/dynamic-price-rounded.pipe';

export interface AreaQuantityButtonBoat extends BoatAreaSource {
    name?: string | null;
}

export interface AreaQuantityButtonBerth {
    length?: number | null;
    width?: number | null;
}

export interface AreaQuantityButtonSources {
    boats?: readonly (AreaQuantityButtonBoat | null | undefined)[];
    berths?: readonly (AreaQuantityButtonBerth | null | undefined)[];
}

/**
 * The "Übernehme die Menge von:" chips offered next to a m² quantity field, in one place for every
 * dialog that offers them (Bierdeckel, Abo, both booking dialogs). Areas resolve through
 * `resolveBoatArea()` so the offered quantity is the same number the invoice is billed with.
 *
 * Entries without a resolvable area drop out. With several boats the chip carries the boat name -
 * on a person's Bierdeckel the plain label could not tell two boats apart.
 */
export function buildAreaQuantityButtons(sources: AreaQuantityButtonSources): ProduktQuantityButton[] {

    const buttons: ProduktQuantityButton[] = [];

    const berths = sources.berths || [];

    // Numbered over the ORIGINAL list, not the surviving entries: a berth-less occupancy or one
    // without dimensions must not renumber the chips away from the Belegung the user sees.
    berths.forEach((berth, index) => {
        const quantity = resolveBerthArea(berth);

        if (!quantity) {
            return;
        }

        buttons.push({
            label: berths.length > 1
                ? TolgeeService.translate('bcm.positions.label.berthOccupancyArea', {
                    index: String(index + 1),
                    quantity: String(quantity),
                })
                : TolgeeService.translate('bcmBerths.area', {quantity: String(quantity)}),
            quantity,
        });
    });

    const boats = (sources.boats || []).filter(Boolean);

    boats.forEach(boat => {
        const quantity = resolveBoatArea(boat);

        if (!quantity) {
            return;
        }

        buttons.push({
            label: (boats.length > 1 && boat.name)
                ? TolgeeService.translate('boats.areaNamed', {name: boat.name, quantity: String(quantity)})
                : TolgeeService.translate('boats.area', {quantity: String(quantity)}),
            quantity,
        });
    });

    return buttons;
}

function resolveBerthArea(berth: AreaQuantityButtonBerth): number | null {
    const length = Number(berth?.length);
    const width = Number(berth?.width);

    if (!Number.isFinite(length) || length <= 0 || !Number.isFinite(width) || width <= 0) {
        return null;
    }

    return roundNumber(length * width, RoundNumberFactor.TwoDecimals);
}
